import { type FC, useCallback, useMemo, useState } from "react";

import { SearchableCard } from "@snapchat/snap-design-system";
import { flow } from "fp-ts/lib/function";
import AutoSizer from "react-virtualized-auto-sizer";

import { type RemoteApiSpec, type SpecApprovalState } from "../../../gql-generated/types";
import { ListContainer, SearchContainer } from "../../../layout/components";
import { useDeleteRemoteApiSpec } from "../../hooks";
import { useRemoteApiSpecs } from "../../hooks/useRemoteApiSpecs";

import { VirtualizedContainer, apisListStyles, searchContainerStyle } from "./ApisList.styles";
import { ApisListHeader } from "./header";
import { LoadableApiRow } from "./row";
import { searchApis } from "./search";
import { SortBy, SortDirection, onSortHelper } from "./sort";
import { VirtualizedList } from "./virtualized-list/components";

const APIS_HEIGHT = 48;

const searchRemoteApis =
    (text: string) =>
    (apis: readonly RemoteApiSpec[]): readonly RemoteApiSpec[] => {
        if (text) {
            return searchApis(apis, text);
        }

        return apis;
    };

const applyStatusFilters =
    (filters: Set<SpecApprovalState>) =>
    (apis: readonly RemoteApiSpec[]): readonly RemoteApiSpec[] => {
        if (filters.size > 0) {
            return apis.filter((api) => {
                return filters.has(api.approvalState);
            });
        }

        return apis;
    };

const sortApis =
    (sortBy: SortBy, sortDirection: SortDirection) =>
    (apis: readonly RemoteApiSpec[]): readonly RemoteApiSpec[] => {
        return onSortHelper(sortBy, sortDirection, apis);
    };

const applyFiltersAndSort = (
    apis: readonly RemoteApiSpec[],
    search: string,
    statusFilters: Set<SpecApprovalState>,
    sortBy: SortBy,
    sortDirection: SortDirection,
): readonly RemoteApiSpec[] => {
    return flow(searchRemoteApis(search), applyStatusFilters(statusFilters), sortApis(sortBy, sortDirection))(apis);
};

export const ApisList: FC = () => {
    const { specs, loading: loadingPage } = useRemoteApiSpecs();
    const { deleteRemoteApiSpec, loading: deleteLoading } = useDeleteRemoteApiSpec();

    const [search, setSearch] = useState("");
    const [statusFilters, setStatusFilters] = useState(new Set<SpecApprovalState>());

    const [sortDirection, setSortDirection] = useState(SortDirection.Desc);
    const [sortBy, setSortBy] = useState(SortBy.Date);

    const filteredApis = useMemo(
        () => applyFiltersAndSort(specs, search, statusFilters, sortBy, sortDirection),
        [specs, search, statusFilters, sortBy, sortDirection],
    );

    const onSort = useCallback((sortBy: SortBy, sortDirection: SortDirection) => {
        setSortBy(sortBy);
        setSortDirection(sortDirection);
    }, []);

    const onDelete = useCallback(
        async (spec: RemoteApiSpec) => {
            await deleteRemoteApiSpec(spec.id);
        },
        [deleteRemoteApiSpec],
    );

    return (
        <ListContainer className={apisListStyles}>
            <SearchableCard
                searchPlaceholder="Search APIs"
                padBody={false}
                onSearch={setSearch}
                searchValue={search}
                className={searchContainerStyle}
            >
                <ApisListHeader
                    onSort={onSort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onStatusFilter={setStatusFilters}
                    statusFilters={statusFilters}
                />
                {/* change to fixed size list */}
                <VirtualizedContainer>
                    <SearchContainer loading={loadingPage} noResults={filteredApis.length === 0} height={"100%"}>
                        <AutoSizer>
                            {({ height, width }) => (
                                <VirtualizedList<RemoteApiSpec, RemoteApiSpec[]>
                                    data={{
                                        list: filteredApis,
                                        onDelete,
                                        deleteLoading,
                                    }}
                                    hasMore={false}
                                    itemSize={APIS_HEIGHT}
                                    height={height}
                                    width={width}
                                >
                                    {LoadableApiRow}
                                </VirtualizedList>
                            )}
                        </AutoSizer>
                    </SearchContainer>
                </VirtualizedContainer>
            </SearchableCard>
        </ListContainer>
    );
};
